import React, { useState } from 'react';
import { Send, Bot, X, Lock, AlertCircle } from 'lucide-react';
import OpenAI from 'openai';
import { insuranceKnowledge } from '../data/insuranceKnowledge';

const openai = new OpenAI({
  apiKey: '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',
  dangerouslyAllowBrowser: true
});

const SYSTEM_MESSAGE = {
  role: 'system',
  content: `Vous êtes l'assistant virtuel de l'ASDA Genève, spécialisé dans le domaine de l'assurance en Suisse. 
  
  Voici la base de connaissances à utiliser pour vos réponses :
  ${JSON.stringify(insuranceKnowledge, null, 2)}
  
  Vos réponses doivent être basées sur :
  1. La base de connaissances ci-dessus
  2. Les informations des sources suivantes :
     - Association pour la formation professionnelle en assurance (VBV/AFA) : https://www.vbv.ch/fr/
     - Association Suisse d'Assurances (ASA/SVV) : https://www.svv.ch/fr
     - Sites des compagnies d'assurance mentionnées
  
  Directives :
  - Utilisez prioritairement les informations de la base de connaissances
  - Citez vos sources quand vous donnez des informations
  - Restez factuel et précis
  - Si une information n'est pas dans la base de connaissances ou les sources autorisées, indiquez-le poliment
  - Ne gardez pas d'historique des conversations précédentes
  
  Concentrez-vous sur :
  - Le marché suisse de l'assurance
  - La formation et les certifications
  - Les réglementations
  - Les actualités et tendances du secteur`
};

// Simple password system
const PASSWORDS = {
  admin: 'ASDA-Admin-2025!',
  member: 'ASDA-Member-2025!'
};

const ATTEMPTS_LIMIT = 3;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [input, setInput] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'member' | null>(null);

  const isLocked = lockedUntil && Date.now() < lockedUntil;

  const validatePassword = (input: string): 'admin' | 'member' | null => {
    if (input === PASSWORDS.admin) return 'admin';
    if (input === PASSWORDS.member) return 'member';
    return null;
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      const remainingTime = Math.ceil((lockedUntil! - Date.now()) / 1000 / 60);
      setPasswordError(`Accès temporairement bloqué. Réessayez dans ${remainingTime} minutes.`);
      return;
    }

    const role = validatePassword(password);

    if (role) {
      setIsAuthenticated(true);
      setUserRole(role);
      setPasswordError('');
      setPassword('');
      setAttempts(0);
      setLockedUntil(null);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= ATTEMPTS_LIMIT) {
        const lockTime = Date.now() + LOCKOUT_TIME;
        setLockedUntil(lockTime);
        setPasswordError(`Trop de tentatives. Accès bloqué pendant 15 minutes.`);
      } else {
        setPasswordError(`Mot de passe incorrect. ${ATTEMPTS_LIMIT - newAttempts} tentatives restantes.`);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setCurrentQuestion(input);
    setInput('');
    setIsLoading(true);

    try {
      const response = await openai.chat.completions.create({
        model: 'gpt-4',
        messages: [
          SYSTEM_MESSAGE,
          { role: 'user', content: input }
        ],
        temperature: 0.7,
        max_tokens: 500,
      });

      setCurrentAnswer(response.choices[0]?.message?.content || 'Désolé, je n\'ai pas pu traiter votre demande.');
    } catch (error) {
      console.error('Error:', error);
      setCurrentAnswer('Désolé, une erreur est survenue. Veuillez réessayer plus tard.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setIsOpen(false);
    setIsAuthenticated(false);
    setPassword('');
    setPasswordError('');
    setAttempts(0);
    setLockedUntil(null);
    setUserRole(null);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="bg-dark text-beige p-3 rounded-full shadow-lg hover:bg-dark/90 transition-colors duration-300"
      >
        <Bot className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg w-96">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-dark" />
          <h2 className="text-sm font-medium text-dark">Assistant ASDA</h2>
          {isAuthenticated && (
            <span className={`text-xs px-2 py-0.5 rounded ${
              userRole === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {userRole === 'admin' ? 'Admin' : 'Membre'}
            </span>
          )}
        </div>
        <button
          onClick={resetState}
          className="text-dark/60 hover:text-dark"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {!isAuthenticated ? (
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="bg-dark/5 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-6 w-6 text-dark/60" />
            </div>
            <h3 className="text-lg font-medium text-dark mb-2">Accès protégé</h3>
            <p className="text-sm text-dark/60">
              Veuillez entrer votre mot de passe pour accéder au chat
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {passwordError && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p>{passwordError}</p>
              </div>
            )}
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez le mot de passe"
                className="w-full p-2 border border-dark/20 rounded-md focus:border-dark focus:ring-1 focus:ring-dark"
                disabled={isLocked}
              />
              <p className="mt-2 text-xs text-dark/60">
                Le mot de passe doit être exact, en respectant la casse et les caractères spéciaux.
              </p>
            </div>
            <button
              type="submit"
              disabled={isLocked || !password}
              className="w-full bg-dark text-beige p-2 rounded-md hover:bg-dark/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLocked ? 'Accès temporairement bloqué' : 'Accéder au chat'}
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="h-96 overflow-y-auto p-4 space-y-4">
            {!currentQuestion && !currentAnswer && (
              <div className="text-center text-dark/80 my-8">
                <p className="text-sm">Bienvenue ! Je suis l'assistant virtuel de l'ASDA Genève.</p>
              </div>
            )}
            
            {currentQuestion && (
              <div className="p-3 rounded-lg bg-dark text-beige ml-12 text-sm">
                {currentQuestion}
              </div>
            )}
            
            {currentAnswer && (
              <div className="p-3 rounded-lg bg-cream/30 mr-12 text-sm">
                {currentAnswer}
              </div>
            )}

            {isLoading && (
              <div className="flex justify-center">
                <div className="animate-pulse text-dark/60 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-dark rounded-full animate-bounce"></span>
                  <span className="w-1.5 h-1.5 bg-dark rounded-full animate-bounce delay-75"></span>
                  <span className="w-1.5 h-1.5 bg-dark rounded-full animate-bounce delay-150"></span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 text-sm rounded-md border-cream bg-white/50 shadow-sm focus:border-dark focus:ring-dark placeholder-dark/50"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="p-2 bg-dark text-beige rounded-md hover:bg-dark/90 disabled:opacity-50 transition-colors duration-300"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ChatBot;